import { useMemo } from "react";
import { useForm } from "react-hook-form";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { useYupValidationResolver } from "@ryde/redux";
import { errors, Log, RecaptchaEnterpriseActions } from "@ryde/shared";
import {
  apiDataFetchingLandingAtom,
  createMessage,
  ILoginLandingModel,
  landingMessagesAtom,
  LandingStepEnum,
  LoginStepFormFieldsEnum,
  useLandingActions,
  useLandingApiAuth,
  useLandingOtpErrors,
  useLandingRecaptchaToken,
} from "@ryde/utils-landings";

import { yupLoginLandingValidationSchema } from "./parts";

export const useStepLogin = () => {
  const { goToStep } = useLandingActions();
  const { requestOtp } = useLandingApiAuth();
  const { handleErrors } = useLandingOtpErrors();
  const { getRecaptchaToken } = useLandingRecaptchaToken();
  const setServerErrors = useSetRecoilState(landingMessagesAtom);
  const setApiLoading = useSetRecoilState(apiDataFetchingLandingAtom);

  const resolver = useYupValidationResolver(
    yupLoginLandingValidationSchema,
    "landing",
  );

  const methods = useForm<ILoginLandingModel>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      workerId: "",
      phone: "",
    },
    resolver,
  });

  const {
    getValues,
    clearErrors,
    trigger,
    watch,
    formState: { isValid },
  } = methods;

  const workerIdWatchValue = watch(LoginStepFormFieldsEnum.WorkerId);
  const phoneWatchValue = watch(LoginStepFormFieldsEnum.Phone);

  const isContinueCanBeApply = useMemo(() => {
    return isValid && workerIdWatchValue && phoneWatchValue;
  }, [isValid, phoneWatchValue, workerIdWatchValue]);

  const handleLogin = useEvent(async (): Promise<void> => {
    const isValidAfterTrigger = await trigger();

    if (!isValidAfterTrigger) {
      return;
    }

    setApiLoading(true);
    setServerErrors(createMessage([]));
    const phone = getValues(LoginStepFormFieldsEnum.Phone);
    const workerId = getValues(LoginStepFormFieldsEnum.WorkerId);
    const recaptchaToken = await getRecaptchaToken(
      RecaptchaEnterpriseActions.REQUEST_OTP,
    );

    try {
      const requestOtpResponse = await requestOtp({
        recaptchaToken,
        workerId,
        phone,
      });

      const { success, error } = requestOtpResponse ?? {};

      if (success) {
        setServerErrors(createMessage([]));
        goToStep(LandingStepEnum.VerificationStep, {
          workerId,
          phone,
        });
      } else {
        handleErrors({
          error,
          defaultAction(e) {
            setServerErrors(createMessage([errors.auth.errorWhileRequestOTP]));
          },
        });
      }
    } catch (error) {
      Log.warn("Error while request OTP", error);
      setServerErrors(createMessage([errors.auth.errorWhileRequestOTP]));
    } finally {
      setApiLoading(false);
    }
  });

  return { methods, handleLogin, isContinueCanBeApply };
};
